/**
 * GRC API Service
 * Connects to PHP backend at /MVP patent/api/grc.php
 */

import {
  Framework,
  FrameworkSection,
  GRCControl,
  GRCRisk,
  GRCAudit,
  GRCAsset,
  GRCDocument,
  GRCEvidence,
  ApplicabilityQuestion,
  WizardSession,
  WizardAnswer,
  AIAssessment,
  DashboardData,
  APIResponse,
  AIApplicabilityResponse,
  CISCheckScript,
  IngestionProgress
} from '../types/grc';

const API_BASE = '/grc-api';

// Get OpenAI API key from localStorage (set in ISO Settings)
const getApiKey = (): string | null => {
  return localStorage.getItem('iso_openai_key');
};

const api = async <T>(action: string, body?: any): Promise<APIResponse<T>> => {
  try {
    const apiKey = getApiKey();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add API key header if available
    if (apiKey) {
      headers['X-OpenAI-Key'] = apiKey;
    }
    
    const opts: RequestInit = body 
      ? { 
          method: 'POST', 
          headers, 
          body: JSON.stringify(body) 
        } 
      : { 
          method: 'GET',
          headers: apiKey ? { 'X-OpenAI-Key': apiKey } : undefined
        };
    
    const res = await fetch(`${API_BASE}?action=${action}`, opts);
    const data = await res.json();
    
    // Even if HTTP status is not OK, try to parse JSON error
    if (!res.ok && !data.error) {
      data.error = `HTTP ${res.status}: ${res.statusText}`;
      data.success = false;
    }
    
    return data;
  } catch (error) {
    console.error('GRC API Error:', error);
    return { success: false, error: String(error) };
  }
};

// GET-only helper used by platform-users and audit-trail endpoints
const apiGet = async <T>(action: string): Promise<APIResponse<T>> => {
  return api<T>(action);
};

export const grcService = {
  // ─── FRAMEWORKS ────────────────────────────────────────────
  
  getFrameworks: async (): Promise<Framework[]> => {
    const res = await api<Framework[]>('frameworks');
    return res.success ? res.data || [] : [];
  },

  getFrameworksWithStatus: async (): Promise<Framework[]> => {
    const res = await api<Framework[]>('frameworks-status');
    return res.success ? res.data || [] : [];
  },

  getFramework: async (id: number): Promise<Framework | null> => {
    const res = await api<Framework>(`framework&id=${id}`);
    return res.success ? res.data || null : null;
  },

  createFramework: async (framework: {
    name: string;
    code: string;
    framework_type: string;
    version?: string;
    description?: string;
    status?: string;
  }): Promise<{ success: boolean; id?: number; error?: string }> => {
    const res = await api<{ id: number }>('create-framework', framework);
    return {
      success: res.success,
      id: res.data?.id,
      error: res.error
    };
  },

  deleteFramework: async (id: number): Promise<boolean> => {
    const res = await api<any>('delete-framework', { id });
    return res.success;
  },

  /**
   * Download the original source document for a framework.
   * Opens a download link in the browser.
   */
  downloadFramework: (frameworkId: number): void => {
    const downloadUrl = `${API_BASE}?action=download-framework&fw=${frameworkId}`;
    window.open(downloadUrl, '_blank');
  },

  /**
   * Check if a framework has a downloadable source document.
   */
  hasDownloadableSource: async (frameworkId: number): Promise<boolean> => {
    try {
      const logs = await grcService.getIngestionLog(frameworkId);
      return logs.some(log => log.status === 'completed' && log.source_file);
    } catch {
      return false;
    }
  },

  // ─── DOCUMENT INGESTION (AI PROCESSING) ────────────────────
  
  // Initialize ingestion - creates log entry and returns log_id immediately for progress tracking
  initIngestion: async (frameworkId: number, fileName: string): Promise<{
    success: boolean;
    log_id?: number;
    status?: string;
    error?: string;
  }> => {
    try {
      const formData = new FormData();
      formData.append('action', 'init-ingestion');
      formData.append('framework_id', String(frameworkId));
      formData.append('file_name', fileName);
      
      const res = await fetch(`${API_BASE}?action=init-ingestion`, {
        method: 'POST',
        body: formData
      });
      
      return await res.json();
    } catch (error) {
      console.error('Init ingestion error:', error);
      return { success: false, error: String(error) };
    }
  },
  
  ingestDocument: async (frameworkId: number, file: File, logId?: number): Promise<{
    success: boolean;
    pages_processed?: number;
    controls_extracted?: number;
    controls_inserted?: number;
    log_id?: number;
    error?: string;
  }> => {
    try {
      const formData = new FormData();
      formData.append('action', 'ingest-document');
      formData.append('framework_id', String(frameworkId));
      formData.append('document', file);
      if (logId) {
        formData.append('log_id', String(logId));
      }
      
      const apiKey = getApiKey();
      const headers: Record<string, string> = {};
      if (apiKey) headers['X-OpenAI-Key'] = apiKey;

      const res = await fetch(`${API_BASE}?action=ingest-document`, {
        method: 'POST',
        headers,
        body: formData
      });
      
      return await res.json();
    } catch (error) {
      console.error('Ingestion error:', error);
      return { success: false, error: String(error) };
    }
  },

  getIngestionLog: async (frameworkId?: number): Promise<any[]> => {
    const action = frameworkId ? `ingestion-log&fw=${frameworkId}` : 'ingestion-log';
    const res = await api<any[]>(action);
    return res.success ? res.data || [] : [];
  },

  // Poll progress for a specific ingestion job or all active jobs
  getIngestionProgress: async (params?: { logId?: number; frameworkId?: number }): Promise<IngestionProgress[]> => {
    let action = 'ingestion-progress';
    if (params?.logId) action += `&log_id=${params.logId}`;
    else if (params?.frameworkId) action += `&fw=${params.frameworkId}`;
    const res = await api<IngestionProgress[]>(action);
    return res.success ? res.data || [] : [];
  },

  // ─── CONTROLS ──────────────────────────────────────────────
  
  getControls: async (frameworkId: number): Promise<GRCControl[]> => {
    const res = await api<GRCControl[]>(`controls&fw=${frameworkId}`);
    return res.success ? res.data || [] : [];
  },

  getControl: async (id: number): Promise<GRCControl | null> => {
    const res = await api<GRCControl>(`control&id=${id}`);
    return res.success ? res.data || null : null;
  },

  updateControl: async (control: Partial<GRCControl> & { id: number }): Promise<boolean> => {
    const res = await api<any>('update-control', control);
    return res.success;
  },

  reprocessControl: async (controlId: number): Promise<{ success: boolean; fields_updated?: number; error?: string }> => {
    const res = await api<{ fields_updated: number }>('reprocess-control', { control_id: controlId });
    return { success: res.success, fields_updated: res.data?.fields_updated, error: res.error };
  },

  reprocessSection: async (chapterId: number): Promise<{ success: boolean; success_count?: number; error_count?: number; error?: string }> => {
    const res = await api<{ success_count: number; error_count: number }>('reprocess-section', { chapter_id: chapterId });
    return { 
      success: res.success, 
      success_count: res.data?.success_count, 
      error_count: res.data?.error_count,
      error: res.error 
    };
  },

  getSoA: async (frameworkId: number): Promise<GRCControl[]> => {
    const res = await api<GRCControl[]>(`soa&fw=${frameworkId}`);
    return res.success ? res.data || [] : [];
  },

  // ─── RISKS ─────────────────────────────────────────────────
  
  getRisks: async (frameworkId?: number): Promise<GRCRisk[]> => {
    const action = frameworkId ? `risks&fw=${frameworkId}` : 'risks';
    const res = await api<GRCRisk[]>(action);
    return res.success ? res.data || [] : [];
  },

  createRisk: async (risk: Omit<GRCRisk, 'id'>): Promise<number | null> => {
    const res = await api<{ id: number }>('add-risk', risk);
    return res.success ? res.data?.id || null : null;
  },

  updateRisk: async (risk: Partial<GRCRisk> & { id: number }): Promise<boolean> => {
    const res = await api<any>('update-risk', risk);
    return res.success;
  },

  deleteRisk: async (id: number): Promise<boolean> => {
    const res = await api<any>('delete-risk', { id });
    return res.success;
  },

  // ─── AUDITS ────────────────────────────────────────────────
  
  getAudits: async (): Promise<GRCAudit[]> => {
    const res = await api<GRCAudit[]>('audits');
    return res.success ? res.data || [] : [];
  },

  createAudit: async (audit: Omit<GRCAudit, 'id'>): Promise<number | null> => {
    const res = await api<{ id: number }>('add-audit', audit);
    return res.success ? res.data?.id || null : null;
  },

  updateAudit: async (audit: Partial<GRCAudit> & { id: number }): Promise<boolean> => {
    const res = await api<any>('update-audit', audit);
    return res.success;
  },

  // ─── ASSETS ────────────────────────────────────────────────
  
  getAssets: async (): Promise<GRCAsset[]> => {
    const res = await api<GRCAsset[]>('assets');
    return res.success ? res.data || [] : [];
  },

  createAsset: async (asset: Omit<GRCAsset, 'id'>): Promise<number | null> => {
    const res = await api<{ id: number }>('add-asset', asset);
    return res.success ? res.data?.id || null : null;
  },

  updateAsset: async (asset: Partial<GRCAsset> & { id: number }): Promise<boolean> => {
    const res = await api<any>('update-asset', asset);
    return res.success;
  },

  deleteAsset: async (id: number): Promise<boolean> => {
    const res = await api<any>('delete-asset', { id });
    return res.success;
  },

  // ─── DOCUMENTS ─────────────────────────────────────────────
  
  getDocuments: async (frameworkId?: number): Promise<GRCDocument[]> => {
    const action = frameworkId ? `documents&fw=${frameworkId}` : 'documents';
    const res = await api<GRCDocument[]>(action);
    return res.success ? res.data || [] : [];
  },

  createDocument: async (doc: {
    framework_id: number;
    doc_type: string;
    title: string;
    content?: string;
    sections?: any;
    owner?: string;
    status?: string;
    covered_controls?: string[];
  }): Promise<GRCDocument | null> => {
    const res = await api<GRCDocument>('create-document', doc);
    return res.success ? res.data || null : null;
  },

  updateDocument: async (id: number, updates: {
    title?: string;
    content?: string;
    sections?: any;
    owner?: string;
    status?: string;
    covered_controls?: string[];
    version?: string;
  }): Promise<GRCDocument | null> => {
    const res = await api<GRCDocument>('update-document', { id, ...updates });
    return res.success ? res.data || null : null;
  },

  deleteDocument: async (id: number): Promise<boolean> => {
    const res = await api<any>('delete-document', { id });
    return res.success;
  },

  // ─── WIZARD ────────────────────────────────────────────────
  
  getWizardQuestions: async (frameworkId: number): Promise<ApplicabilityQuestion[]> => {
    const res = await api<ApplicabilityQuestion[]>(`wizard-questions&fw=${frameworkId}`);
    return res.success ? res.data || [] : [];
  },

  startWizard: async (frameworkId: number, orgInfo: {
    organization_name: string;
    organization_type: string;
    organization_size: string;
    country: string;
    sector: string;
  }): Promise<WizardSession | null> => {
    const res = await api<WizardSession>('wizard-start', {
      framework_id: frameworkId,
      ...orgInfo
    });
    return res.success ? res.data || null : null;
  },

  getWizardSession: async (sessionKey: string): Promise<WizardSession | null> => {
    const res = await api<WizardSession>(`wizard-session&key=${sessionKey}`);
    return res.success ? res.data || null : null;
  },

  saveWizardAnswer: async (sessionId: number, questionId: number, answerValue: string, confidence?: number, notes?: string): Promise<boolean> => {
    const res = await api<any>('wizard-answer', {
      session_id: sessionId,
      question_id: questionId,
      answer_value: answerValue,
      confidence: confidence || 100,
      notes: notes || null
    });
    return res.success;
  },

  getWizardAnswers: async (sessionId: number): Promise<WizardAnswer[]> => {
    const res = await api<WizardAnswer[]>(`wizard-answers&session=${sessionId}`);
    return res.success ? res.data || [] : [];
  },

  updateWizardProgress: async (sessionId: number, currentStep: number): Promise<boolean> => {
    const res = await api<any>('wizard-progress', {
      session_id: sessionId,
      current_step: currentStep
    });
    return res.success;
  },

  completeWizard: async (sessionId: number): Promise<{ success: boolean; data?: AIAssessment; error?: string }> => {
    const res = await api<AIAssessment>('wizard-complete', {
      session_id: sessionId
    });
    return {
      success: res.success,
      data: res.data,
      error: res.error
    };
  },

  getAIAssessment: async (assessmentId: number): Promise<AIAssessment | null> => {
    const res = await api<AIAssessment>(`ai-assessment&id=${assessmentId}`);
    return res.success ? res.data || null : null;
  },

  resetWizard: async (frameworkId: number): Promise<boolean> => {
    const res = await api<any>('reset-wizard', { framework_id: frameworkId });
    return res.success;
  },

  // ─── DASHBOARD ─────────────────────────────────────────────
  
  getFrameworkDashboard: async (frameworkId: number): Promise<DashboardData | null> => {
    const res = await api<DashboardData>(`framework-dashboard&fw=${frameworkId}`);
    return res.success ? res.data || null : null;
  },

  getDashboard: async (): Promise<any> => {
    const res = await api<any>('dashboard');
    return res.success ? res.data : null;
  },

  getScore: async (): Promise<any> => {
    const res = await api<any>('score');
    return res.success ? res.data : null;
  },

  // ─── EVIDENCE ────────────────────────────────────────────────
  
  getEvidence: async (controlId: number): Promise<GRCEvidence[]> => {
    const res = await api<GRCEvidence[]>(`evidence&control=${controlId}`);
    return res.success ? res.data || [] : [];
  },

  getFrameworkEvidence: async (frameworkId: number): Promise<GRCEvidence[]> => {
    const res = await api<GRCEvidence[]>(`framework-evidence&fw=${frameworkId}`);
    return res.success ? res.data || [] : [];
  },

  addEvidence: async (controlId: number, data: {
    file?: File;
    description?: string;
    comment_text?: string;
    link_url?: string;
    uploaded_by?: string;
  }): Promise<{ success: boolean; evidence_id?: number; ai_validation?: any; error?: string }> => {
    try {
      const formData = new FormData();
      formData.append('control_id', String(controlId));
      if (data.file) formData.append('file', data.file);
      if (data.description) formData.append('description', data.description);
      if (data.comment_text) formData.append('comment_text', data.comment_text);
      if (data.link_url) formData.append('link_url', data.link_url);
      if (data.uploaded_by) formData.append('uploaded_by', data.uploaded_by);

      const apiKey = getApiKey();
      const headers: Record<string, string> = {};
      if (apiKey) headers['X-OpenAI-Key'] = apiKey;

      const res = await fetch(`${API_BASE}?action=add-evidence`, {
        method: 'POST',
        headers,
        body: formData
      });
      return await res.json();
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  reviewEvidence: async (evidenceId: number, status: 'accepted' | 'rejected', reviewedBy?: string): Promise<boolean> => {
    const res = await api<any>('review-evidence', {
      id: evidenceId,
      status,
      reviewed_by: reviewedBy || 'reviewer'
    });
    return res.success;
  },

  deleteEvidence: async (evidenceId: number): Promise<boolean> => {
    const res = await api<any>('delete-evidence', { id: evidenceId });
    return res.success;
  },

  // ─── AI APPLICABILITY ASSESSMENT ────────────────────────────
  
  runAIApplicabilityAssessment: async (frameworkId: number, orgInfo: {
    organization_name: string;
    industry: string;
    size: string;
    services?: string;
    data_types?: string;
    regulatory_requirements?: string;
  }): Promise<AIApplicabilityResponse> => {
    const res = await api<AIApplicabilityResponse>('ai-applicability', {
      framework_id: frameworkId,
      ...orgInfo
    });
    return res.success && res.data ? res.data : {
      success: false,
      results: [],
      summary: res.error || 'AI assessment failed',
      total_applicable: 0,
      total_not_applicable: 0
    };
  },

  validateEvidenceWithAI: async (controlId: number, evidenceId: number): Promise<{
    success: boolean;
    compliance_score?: number;
    validation_result?: string;
    is_compliant?: boolean;
    suggestions?: string[];
    error?: string;
  }> => {
    const res = await api<any>('validate-evidence', {
      control_id: controlId,
      evidence_id: evidenceId
    });
    return res.success ? {
      success: true,
      compliance_score: res.data?.compliance_score,
      validation_result: res.data?.validation_result,
      is_compliant: res.data?.is_compliant,
      suggestions: res.data?.suggestions
    } : {
      success: false,
      error: res.error
    };
  },

  // ─── CIS BENCHMARK SCRIPTS ────────────────────────────────
  
  getCISCheckScript: async (controlId: number, scriptType?: 'powershell' | 'bash' | 'python'): Promise<CISCheckScript | null> => {
    const action = scriptType ? `cis-script&control=${controlId}&type=${scriptType}` : `cis-script&control=${controlId}`;
    const res = await api<CISCheckScript>(action);
    return res.success ? res.data || null : null;
  },

  generateCISScript: async (controlId: number, scriptType: 'powershell' | 'bash' | 'python'): Promise<{
    success: boolean;
    script?: CISCheckScript;
    error?: string;
  }> => {
    const res = await api<CISCheckScript>('generate-cis-script', {
      control_id: controlId,
      script_type: scriptType
    });
    return {
      success: res.success,
      script: res.data,
      error: res.error
    };
  },

  // ─── UNIFIED ASSETS (cross-framework) ────────────────────────
  
  getGlobalAssets: async (): Promise<GRCAsset[]> => {
    const res = await api<GRCAsset[]>('assets');
    return res.success ? res.data || [] : [];
  },

  // ─── UNIFIED RISKS (cross-framework) ────────────────────────
  
  getGlobalRisks: async (): Promise<GRCRisk[]> => {
    const res = await api<GRCRisk[]>('risks');
    return res.success ? res.data || [] : [];
  },

  // ─── DYNAMIC FRAMEWORK SECTIONS ────────────────────────────

  getFrameworkSections: async (frameworkId: number): Promise<FrameworkSection[]> => {
    const res = await api<FrameworkSection[]>('framework-sections', { framework_id: frameworkId });
    return res.success ? res.data || [] : [];
  },

  updateSection: async (sectionId: number, data: Partial<FrameworkSection>): Promise<boolean> => {
    const res = await api<any>('update-section', { section_id: sectionId, ...data });
    return res.success;
  },

  createSection: async (frameworkId: number, data: { title: string; description?: string; icon?: string; section_type?: string; is_informational?: number }): Promise<{ success: boolean; id?: number }> => {
    const res = await api<any>('create-section', { framework_id: frameworkId, ...data });
    return { success: res.success, id: res.id };
  },

  deleteSection: async (sectionId: number): Promise<boolean> => {
    const res = await api<any>('delete-section', { id: sectionId });
    return res.success;
  },

  createControl: async (frameworkId: number, data: { control_ref?: string; title?: string; description?: string; section_id?: number; check_type?: string; severity?: string }): Promise<{ success: boolean; id?: number }> => {
    const res = await api<any>('create-control', { framework_id: frameworkId, ...data });
    return { success: res.success, id: res.id };
  },

  deleteControl: async (controlId: number): Promise<boolean> => {
    const res = await api<any>('delete-control', { id: controlId });
    return res.success;
  },

  toggleSectionInformational: async (sectionId: number, frameworkId: number, controlIds?: number[]): Promise<{ success: boolean; is_informational: number; message: string }> => {
    const body: Record<string, unknown> = { section_id: sectionId, framework_id: frameworkId };
    if (controlIds && controlIds.length > 0) body.control_ids = controlIds;
    const res = await api<any>('toggle-section-informational', body);
    return {
      success: res.success,
      is_informational: res.data?.is_informational ?? 0,
      message: res.data?.message ?? ''
    };
  },

  ensureSections: async (frameworkId: number, sections: Array<{ key: string; label: string; icon: string; description: string; chapter_codes: string[]; is_informational: number }>): Promise<FrameworkSection[]> => {
    const res = await api<FrameworkSection[]>('ensure-sections', { framework_id: frameworkId, sections });
    return res.success ? res.data || [] : [];
  },

  // ─── AI REGULATION ASSISTANT ────────────────────────────────

  aiRegulationChat: async (frameworkId: number, question: string): Promise<{ success: boolean; answer: string; disclaimer: string; error?: string }> => {
    const res = await api<any>('ai-regulation-chat', { framework_id: frameworkId, question });
    return {
      success: res.success,
      answer: res.data?.answer ?? '',
      disclaimer: res.data?.disclaimer ?? '',
      error: res.error
    };
  },

  // ─── PLATFORM USERS ────────────────────────────────────────

  getPlatformUsers: async (): Promise<any[]> => {
    const res = await apiGet<any>('platform-users');
    return res.data || [];
  },

  createPlatformUser: async (user: { username: string; email: string; full_name?: string; name?: string; role: string; status: string; password?: string }): Promise<{ success: boolean; data?: any }> => {
    const res = await api<any>('create-platform-user', user);
    return res;
  },

  updatePlatformUser: async (user: { id: number; username?: string; email?: string; full_name?: string; name?: string; role?: string; status?: string; password?: string }): Promise<{ success: boolean }> => {
    return await api<any>('update-platform-user', user);
  },

  deletePlatformUser: async (id: number): Promise<{ success: boolean }> => {
    return await api<any>('delete-platform-user', { id });
  },

  // ─── AUDIT TRAIL ───────────────────────────────────────────

  getAuditTrail: async (limit: number = 100, offset: number = 0): Promise<{ data: any[]; total: number }> => {
    const res = await apiGet<any>(`audit-trail&limit=${limit}&offset=${offset}`);
    return { data: res.data || [], total: res.total || 0 };
  },

  logActivity: async (action: string, details: string, user: string = ''): Promise<void> => {
    await api<any>('log-activity', { action, details, user });
  },

  // ─── CROSS-FRAMEWORK CONTROL MAPPINGS ────────────────────────

  /**
   * Get all mappings for a specific control
   */
  getControlMappings: async (controlId: number): Promise<Array<{
    id: number;
    source_control_id: number;
    target_control_id: number;
    source_code: string;
    source_title: string;
    source_framework_id: number;
    source_framework_code: string;
    source_framework_name: string;
    target_code: string;
    target_title: string;
    target_description: string;
    target_status: string;
    target_fulfillment: string;
    target_framework_id: number;
    target_framework_code: string;
    target_framework_name: string;
    mapping_type: 'equivalent' | 'overlapping' | 'partial' | 'related';
    relevance_score: number;
    explanation: string;
    created_by_ai: boolean;
  }>> => {
    try {
      const res = await fetch(`/api/control-mappings.php?control_id=${controlId}`);
      const data = await res.json();
      return data.success ? data.mappings : [];
    } catch (error) {
      console.error('Error fetching control mappings:', error);
      return [];
    }
  },

  /**
   * Get all cross-framework mappings for a framework
   */
  getFrameworkMappings: async (frameworkId: number, targetFrameworkId?: number): Promise<Array<{
    source_control_id: number;
    source_code: string;
    source_title: string;
    source_framework_code: string;
    mappings: Array<{
      id: number;
      target_control_id: number;
      target_code: string;
      target_title: string;
      target_framework_id: number;
      target_framework_code: string;
      target_framework_name: string;
      mapping_type: string;
      relevance_score: number;
      explanation: string;
      created_by_ai: boolean;
    }>;
  }>> => {
    try {
      let url = `/api/control-mappings.php?framework_id=${frameworkId}`;
      if (targetFrameworkId) url += `&target_framework_id=${targetFrameworkId}`;
      const res = await fetch(url);
      const data = await res.json();
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching framework mappings:', error);
      return [];
    }
  },

  /**
   * Create or update control mappings (bulk supported)
   */
  saveControlMappings: async (mappings: Array<{
    source_control_id: number;
    target_control_id: number;
    mapping_type: 'equivalent' | 'overlapping' | 'partial' | 'related';
    relevance_score: number;
    explanation: string;
    created_by_ai?: boolean;
  }>): Promise<{ success: boolean; created: number; updated: number }> => {
    try {
      const res = await fetch('/api/control-mappings.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings })
      });
      return await res.json();
    } catch (error) {
      console.error('Error saving control mappings:', error);
      return { success: false, created: 0, updated: 0 };
    }
  },

  /**
   * Delete a control mapping
   */
  deleteControlMapping: async (id: number): Promise<{ success: boolean }> => {
    try {
      const res = await fetch(`/api/control-mappings.php?id=${id}`, { method: 'DELETE' });
      return await res.json();
    } catch (error) {
      console.error('Error deleting control mapping:', error);
      return { success: false };
    }
  },

  /**
   * Get controls from another framework (for mapping selector)
   */
  getControlsForMapping: async (frameworkId: number): Promise<Array<{
    id: number;
    code: string;
    title: string;
    description: string;
  }>> => {
    try {
      const controls = await grcService.getSoA(frameworkId);
      return controls.map(c => ({
        id: c.id,
        code: c.control_code || c.article_code || `#${c.id}`,
        title: c.title,
        description: c.description || ''
      }));
    } catch (error) {
      console.error('Error fetching controls for mapping:', error);
      return [];
    }
  },

  /**
   * Verify extraction quality for a framework
   * Returns detailed analysis of what was extracted and any issues
   */
  verifyExtraction: async (frameworkId: number): Promise<{
    success: boolean;
    framework?: { id: number; name: string; code: string; type: string };
    ingestion?: { status: string; pages_processed: number; controls_found: number; completed_at: string };
    stats?: {
      total_controls: number;
      chapters_count: number;
      sections_count: number;
      controls_with_chapters: number;
      controls_without_chapters: number;
      duplicate_codes: string[];
      missing_descriptions: number;
      very_short_titles: number;
      empty_sections: string[];
      control_code_patterns: Record<string, number>;
    };
    chapters?: Array<{ chapter_code: string; title: string }>;
    sections?: Array<{ id: number; label: string; chapter_codes: string[] }>;
    issues?: Array<{ severity: 'high' | 'medium' | 'low'; message: string }>;
    sample_controls?: any[];
    error?: string;
  }> => {
    const res = await api<any>(`verify-extraction&framework_id=${frameworkId}`);
    return res;
  },

  /**
   * Re-process sections for a framework
   * This will analyze control codes and create proper sections
   */
  reprocessSections: async (frameworkId: number, deleteExisting: boolean = true): Promise<{
    success: boolean;
    chapters_created?: number;
    sections_created?: number;
    inferred_chapters?: string[];
    error?: string;
  }> => {
    const res = await api<any>('reprocess-sections', { framework_id: frameworkId, delete_existing: deleteExisting });
    return res;
  },

  // ─── CONTROL EVIDENCE ────────────────────────────────────────

  /**
   * Get all evidence attached to a specific control
   */
  getControlEvidence: async (controlId: number): Promise<any[]> => {
    const res = await api<any[]>(`control-evidence&control_id=${controlId}`);
    return res.success ? res.data || [] : [];
  },

  // ─── AI COMPLIANCE VERIFICATION ────────────────────────────────

  /**
   * AI-powered compliance verification for a control
   * Analyzes attached evidence (documents, images, text descriptions) against control requirements
   * Returns detailed compliance assessment with findings and recommendations
   */
  verifyControlCompliance: async (controlId: number, frameworkId: number): Promise<{
    success: boolean;
    verification?: {
      overall_status: 'compliant' | 'partial' | 'non_compliant' | 'needs_evidence';
      confidence: number;
      summary: string;
      findings: Array<{
        type: 'pass' | 'warning' | 'fail' | 'info';
        message: string;
      }>;
      recommendations: string[];
      evidence_analysis: Array<{
        document_name: string;
        relevance: 'high' | 'medium' | 'low';
        notes: string;
      }>;
      suggested_fulfillment_status: 'fulfilled' | 'partially_fulfilled' | 'not_fulfilled';
      suggested_compliance_status: 'compliant' | 'partial' | 'non_compliant';
    };
    error?: string;
  }> => {
    try {
      const apiKey = getApiKey();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (apiKey) headers['X-OpenAI-Key'] = apiKey;

      const res = await fetch(`${API_BASE}?action=verify-control-compliance`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ control_id: controlId, framework_id: frameworkId })
      });
      return await res.json();
    } catch (error) {
      return { success: false, error: String(error) };
    }
  }
};
